import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';

/**
 * Data access for the translation editor.
 *
 * `LocalizationApiController` was already complete, so nothing here changes the
 * backend. The shape worth knowing: a locale file is **one file per resource,
 * holding every culture** — `Dictionary<key, Dictionary<culture, value>>` — not
 * one file per language. So a culture tab picks which column of the same
 * document is being edited, and a save writes the whole document back.
 */

const BASE = '/api/localization';

export interface LocalizationCulture {
  code: string;
  /** The culture's own name for itself, e.g. "italiano". */
  name: string;
  englishName: string;
  /** Region code, used by the Razor page to pick a flag. */
  flag: string;
}

export interface CulturesResponse {
  cultures: LocalizationCulture[];
  defaultCulture: string;
  currentCulture: string;
}

/** key → culture → value. */
export type Translations = Record<string, Record<string, string>>;

export interface LocaleFile {
  fileName: string;
  translations: Translations;
  lastModified: string;
  source: string;
  culture: string;
}

/**
 * Keys beginning with an underscore are file metadata, not translations.
 *
 * `GetCrudTranslationsResolved` filters them with the same rule, so the editor
 * hides them too rather than inviting someone to translate `_comment`.
 */
export function isMetadataKey(key: string): boolean {
  return key.startsWith('_');
}

export type TranslationStatus = 'complete' | 'missing';

/** A key is complete when every supported culture has a non-empty value. */
export function translationStatus(
  values: Record<string, string> | undefined,
  cultures: string[],
): TranslationStatus {
  if (!values) return 'missing';
  return cultures.every((code) => (values[code] ?? '').trim().length > 0)
    ? 'complete'
    : 'missing';
}

export const localizationKeys = {
  all: ['admin', 'localization'] as const,
  cultures: ['admin', 'localization', 'cultures'] as const,
  files: ['admin', 'localization', 'files'] as const,
  file: (fileName: string) => ['admin', 'localization', 'file', fileName] as const,
};

export function useCultures() {
  return useQuery<CulturesResponse>({
    queryKey: localizationKeys.cultures,
    queryFn: () => api.get<CulturesResponse>(`${BASE}/cultures`),
    staleTime: Infinity,
  });
}

/**
 * The available locale files.
 *
 * The endpoint wraps the list — `{ files, count }` — rather than returning a
 * bare array, unlike `cultures` which nests under `cultures`. Unwrapped here so
 * callers get the list itself.
 */
interface LocaleFilesResponse {
  files: string[];
  count: number;
}

export function useLocaleFiles() {
  return useQuery<string[]>({
    queryKey: localizationKeys.files,
    queryFn: async () => (await api.get<LocaleFilesResponse>(`${BASE}/files`)).files ?? [],
  });
}

export function useLocaleFile(fileName: string | null) {
  return useQuery<LocaleFile>({
    queryKey: localizationKeys.file(fileName ?? ''),
    queryFn: () => api.get<LocaleFile>(`${BASE}/files/${encodeURIComponent(fileName ?? '')}`),
    enabled: fileName != null && fileName.length > 0,
  });
}

/**
 * Invalidates the edited file and the app's own translations.
 *
 * i18next holds its own copy of the locale JSON, fetched over `/myui/locales`,
 * so a saved change is not visible in the running UI until that reloads. The
 * server-side cache is cleared separately — see `useClearLocalizationCache`.
 */
function useInvalidateFile() {
  const queryClient = useQueryClient();
  return (fileName: string) => {
    queryClient.invalidateQueries({ queryKey: localizationKeys.file(fileName) });
  };
}

/** Writes the whole document back — the endpoint takes the full translations map. */
export function useUpdateLocaleFile() {
  const invalidate = useInvalidateFile();
  return useMutation({
    mutationFn: ({ fileName, translations }: { fileName: string; translations: Translations }) =>
      api.put<void>(`${BASE}/files/${encodeURIComponent(fileName)}`, { translations }),
    onSuccess: (_data, variables) => invalidate(variables.fileName),
  });
}

export function useAddLocaleKey() {
  const invalidate = useInvalidateFile();
  return useMutation({
    mutationFn: ({
      fileName,
      key,
      values,
    }: {
      fileName: string;
      key: string;
      values: Record<string, string>;
    }) => api.post<void>(`${BASE}/files/${encodeURIComponent(fileName)}/keys`, { key, values }),
    onSuccess: (_data, variables) => invalidate(variables.fileName),
  });
}

export function useDeleteLocaleKey() {
  const invalidate = useInvalidateFile();
  return useMutation({
    mutationFn: ({ fileName, key }: { fileName: string; key: string }) =>
      api.delete<void>(
        `${BASE}/files/${encodeURIComponent(fileName)}/keys/${encodeURIComponent(key)}`,
      ),
    onSuccess: (_data, variables) => invalidate(variables.fileName),
  });
}

/** Drops the server's locale cache so the change reaches every request. */
export function useClearLocalizationCache() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<void>(`${BASE}/cache/clear`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: localizationKeys.all }),
  });
}
